import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    ref,
    set,
    onValue,
    onDisconnect,
    serverTimestamp,
    query,
    orderByKey,
    limitToLast,
    Unsubscribe,
} from 'firebase/database';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { rtdb } from '../providers/firebase/firebase';
import type { TradePolymarket } from '../types/types';
import { usePresenceInstanceId } from './usePresenceInstanceId';

const MAX_TRADES = 200;

/** Sanitize an RTDB path segment (mirrors bogg's sanitize_rtdb_path_segment). */
function sanitizePathSegment(id: string): string {
    return id
        .replace(/\+/g, '-plus-')
        .replace(/@/g, '-at-')
        .replace(/\./g, '-dot-')
        .replace(/[/#$[\]]/g, '_');
}

/**
 * Subscribe to live Polymarket trades for specific accounts via Firebase RTDB.
 *
 * - Writes presence at `presence/polymarket_account_trades/{uid}/{instanceId}` with watched account list
 * - Subscribes to `polymarket_account_trades/{safe_uid}/{safe_account_id}` for each account
 * - Backend writes trades where the account is maker OR taker
 * - Uses `orderByKey().limitToLast(N)` to bound reads
 *
 * The returned `trades` Map is keyed by the account id exactly as passed in
 * `accountIds`. Callers are responsible for normalizing ids before calling
 * this hook (e.g. via `toChecksumId`).
 *
 * @param accountIds  Array of wallet addresses to watch (empty = no subscription)
 */
export function useAccountTradesStream(
    accountIds: string[],
): { trades: Map<string, TradePolymarket[]>; connected: boolean } {
    const [trades, setTrades] = useState<Map<string, TradePolymarket[]>>(new Map());
    const [connected, setConnected] = useState(false);
    const unsubAuthRef = useRef<(() => void) | null>(null);
    const unsubDataRef = useRef<Unsubscribe[]>([]);
    const presenceRefPath = useRef<string | null>(null);
    const instanceId = usePresenceInstanceId();
    // Trades seen this session, keyed by accountId then RTDB trade key. RTDB is
    // a cache of the tail, not the source of truth: entries expire by TTL, so a
    // key's absence from a snapshot must never remove a rendered row. Anything
    // older than this session's start is served by the historical fetch, which
    // the sidebar merges with these by transaction_id.
    const storeRef = useRef<Map<string, Map<string, TradePolymarket>>>(new Map());

    const accountsKey = useMemo(() => [...accountIds].sort().join(','), [accountIds]);

    const cleanup = useCallback(() => {
        for (const unsub of unsubDataRef.current) unsub();
        unsubDataRef.current = [];

        if (presenceRefPath.current) {
            set(ref(rtdb, presenceRefPath.current), null);
            presenceRefPath.current = null;
        }

        unsubAuthRef.current?.();
        unsubAuthRef.current = null;

        setConnected(false);
        setTrades(new Map());
        storeRef.current.clear();
    }, []);

    useEffect(() => {
        if (accountIds.length === 0) return;

        let cancelled = false;
        const auth = getAuth();

        function subscribe() {
            if (cancelled) return;

            const uid = auth.currentUser?.uid;
            if (!uid) return;

            const safeUid = sanitizePathSegment(uid);

            // Write presence with watched accounts, keyed per hook instance so
            // multiple tabs of the same user do not overwrite each other.
            const pPath = `presence/polymarket_account_trades/${uid}/${instanceId}`;
            presenceRefPath.current = pPath;
            const presenceRef = ref(rtdb, pPath);
            set(presenceRef, {
                online: true,
                connectedAt: serverTimestamp(),
                account_ids: accountIds,
            });
            onDisconnect(presenceRef).remove();

            // Subscribe to each account's trade path
            for (const accountId of accountIds) {
                const safeAccountId = sanitizePathSegment(accountId);
                const dataRef = query(
                    ref(rtdb, `polymarket_account_trades/${safeUid}/${safeAccountId}`),
                    orderByKey(),
                    limitToLast(MAX_TRADES),
                );

                let firstSnapshot = true;

                const unsub = onValue(
                    dataRef,
                    (snapshot) => {
                        if (firstSnapshot) {
                            setConnected(true);
                            firstSnapshot = false;
                        }
                        const val = snapshot.val() as Record<string, TradePolymarket> | null;
                        if (!val) return; // absence is expiry, not deletion

                        const bucket = storeRef.current.get(accountId) ?? new Map();
                        let added = false;
                        for (const [key, trade] of Object.entries(val)) {
                            if (!bucket.has(key)) {
                                bucket.set(key, trade);
                                added = true;
                            }
                        }
                        if (!added) return;

                        // Keys are `{ts_millis}_{tx}` and ms timestamps are 13
                        // digits, so lexicographic order is chronological.
                        const newest = [...bucket.entries()]
                            .sort((a, b) => (a[0] < b[0] ? 1 : -1))
                            .slice(0, MAX_TRADES);
                        storeRef.current.set(accountId, new Map(newest));

                        setTrades(prev => {
                            const next = new Map(prev);
                            next.set(accountId, newest.map(([, t]) => t));
                            return next;
                        });
                    },
                    (error) => {
                        console.error(`RTDB account_trades error for ${accountId}:`, error);
                        setConnected(false);
                    },
                );
                unsubDataRef.current.push(unsub);
            }
        }

        if (auth.currentUser) {
            subscribe();
        } else {
            const unsubAuth = onAuthStateChanged(auth, (user) => {
                if (user && !cancelled) {
                    unsubAuth();
                    unsubAuthRef.current = null;
                    subscribe();
                }
            });
            unsubAuthRef.current = unsubAuth;
        }

        return () => {
            cancelled = true;
            cleanup();
        };
    }, [accountsKey, cleanup]); // eslint-disable-line react-hooks/exhaustive-deps

    return { trades, connected };
}
